import { Dialog, VisuallyHidden } from "radix-ui";
import { useState } from "react";

import styles from "./Runners.module.css";
import { Button } from "../../components/Button";
import { Chip } from "../../components/Chip";
import { Pill } from "../../components/Pill";
import type { Runner } from "../../data/api/runners";
import {
  isReporting,
  rotateRunnerKey,
  RUNNER_STATUS_META,
  runnerSlots,
} from "../../data/api/runners";
import { Icon } from "../../icons";
import { cx } from "../../lib/cx";

const PanelMeter = ({
  label,
  value,
}: {
  readonly label: string;
  readonly value: number | null;
}) => {
  const tone =
    value === null
      ? undefined
      : value >= 85
        ? styles.tHot
        : value >= 60
          ? styles.tWarm
          : styles.tCool;
  return (
    <div>
      <div className={styles.rpanelMeterTop}>
        <span>{label}</span>
        <span className={styles.meterVal}>
          {value === null ? "—" : `${value}%`}
        </span>
      </div>
      <span className={styles.meterTrack}>
        <i
          className={cx(styles.meterFill, tone)}
          style={{ width: `${value ?? 0}%` }}
        />
      </span>
    </div>
  );
};

export type RunnerPanelProps = {
  // Admin or owner. A member reads the panel; every action is hidden
  // (runners.md §Permissions).
  readonly canManage: boolean;
  readonly onClose: () => void;
  readonly onDrain: () => void;
  readonly onRemove: () => void;
  readonly onRestart: () => void;
  readonly onResume: () => void;
  readonly runner: Runner;
};

export const RunnerPanel = ({
  canManage,
  onClose,
  onDrain,
  onRemove,
  onRestart,
  onResume,
  runner: r,
}: RunnerPanelProps) => {
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [rotatedToken, setRotatedToken] = useState<string | null>(null);
  const [rotating, setRotating] = useState(false);
  const [copied, setCopied] = useState(false);
  const meta = RUNNER_STATUS_META[r.status];
  const reporting = isReporting(r.status);
  // A restart bounces the runtime; there is nothing to bounce on a host that
  // is offline or has never connected (runners.md unifies unconnected here).
  const canRestart = r.status === "online" || r.status === "draining";
  // Tinsmith launches and retires cloud runners; the server refuses every
  // mutation on one (cloud-runners.md §On the Runners page).
  const showActions = canManage && !r.cloud;

  const rotate = async (): Promise<void> => {
    if (rotating) {
      return;
    }
    setRotating(true);
    try {
      const result = await rotateRunnerKey(r.id);
      setRotatedToken(result.token);
    } finally {
      setRotating(false);
    }
  };

  const copyKey = (): void => {
    if (!rotatedToken) {
      return;
    }
    void navigator.clipboard?.writeText(rotatedToken);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Dialog.Root
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
      open
    >
      <Dialog.Portal>
        <Dialog.Overlay className={styles.scrim} />
        <Dialog.Content aria-describedby={undefined} className={styles.rpanel}>
          <VisuallyHidden.Root>
            <Dialog.Title>{r.name}</Dialog.Title>
          </VisuallyHidden.Root>

          <div className={styles.rpanelHead}>
            <span className={styles.ico} style={{ height: 34, width: 34 }}>
              <Icon.Cpu size={17} />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className={styles.rpanelName}>{r.name}</div>
              <div className={styles.sub}>
                {r.vcpu} vCPU · {r.memoryGb} GB
              </div>
            </div>
            <Button icon onClick={onClose} variant="ghost">
              <Icon.X size={15} />
            </Button>
          </div>

          <div className={styles.rpanelStatusline}>
            <Pill
              className={!meta.pill ? styles.off : undefined}
              dotColor={meta.dotColor}
              status={meta.pill}
            >
              {meta.label}
            </Pill>
            {r.cloud ? <Chip>Cloud · {r.cloud.size}</Chip> : null}
            <span className={styles.grow} />
            <span className={styles.sub}>
              v{r.version ?? "—"} · up {r.uptime}
            </span>
          </div>

          <div className={styles.rpanelSection}>
            <div className={styles.rpanelLabel}>Live utilization</div>
            <div className={styles.rpanelMeters}>
              <PanelMeter label="CPU" value={r.cpu} />
              <PanelMeter label="Memory" value={r.mem} />
              <PanelMeter label="Disk" value={r.disk} />
            </div>
          </div>

          <div className={styles.rpanelSection}>
            <div className={styles.rpanelLabel}>Capacity</div>
            <dl className={styles.rpanelSpecs}>
              {/* Agents against advertised slots — `3/4` — so the panel says
                  how much of the runner's configured capacity is in use, not
                  just how busy it is right now. */}
              <div>
                <dt>Agents</dt>
                <dd>{runnerSlots(r)}</dd>
              </div>
              <div>
                <dt>vCPU</dt>
                <dd>{r.vcpu}</dd>
              </div>
              <div>
                <dt>Memory</dt>
                <dd>{r.memoryGb} GB</dd>
              </div>
              <div>
                <dt>Runtime</dt>
                <dd>tinsmith-runner v{r.version ?? "—"}</dd>
              </div>
            </dl>
          </div>

          {reporting && r.agents > 0 ? (
            <div className={styles.rpanelSection}>
              <div className={styles.rpanelLabel}>
                {r.agents} agent{r.agents === 1 ? "" : "s"} running here
              </div>
              <div className={styles.rpanelHint}>
                {r.cloud
                  ? "Tinsmith stops this runner once it has been empty for the idle timeout in Settings → Runners."
                  : "Draining stops new placements and lets these finish before the machine idles."}
              </div>
            </div>
          ) : null}

          {rotatedToken ? (
            <div className={styles.rpanelSection}>
              <div className={styles.rpanelLabel}>New runner key</div>
              <p className={styles.rpanelHint}>
                Shown once — copy it now and reconfigure the host. The previous
                key no longer works.
              </p>
              <div className={styles.addrKey}>
                <code>{rotatedToken}</code>
                <Button onClick={copyKey} size="sm">
                  {copied ? (
                    <>
                      <Icon.Check size={13} /> Copied
                    </>
                  ) : (
                    <>
                      <Icon.Copy size={13} /> Copy
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : null}

          {showActions ? (
            <div className={styles.rpanelActions}>
              {r.status === "online" && (
                <Button onClick={onDrain}>
                  <Icon.Stop size={13} /> Drain
                </Button>
              )}
              {r.status === "draining" && (
                <Button onClick={onResume}>
                  <Icon.Play size={13} /> Resume
                </Button>
              )}
              {canRestart ? (
                <Button disabled={r.restartPending} onClick={onRestart}>
                  <Icon.Refresh size={13} />{" "}
                  {r.restartPending ? "Restart requested" : "Restart"}
                </Button>
              ) : null}
              <Button disabled={rotating} onClick={() => void rotate()}>
                <Icon.Shield size={13} />{" "}
                {rotating ? "Rotating…" : "Rotate key"}
              </Button>
              <span className={styles.grow} />
              {confirmRemove ? (
                <>
                  <Button
                    onClick={() => setConfirmRemove(false)}
                    size="sm"
                    variant="ghost"
                  >
                    Cancel
                  </Button>
                  <Button onClick={onRemove} size="sm" variant="danger">
                    Confirm remove
                  </Button>
                </>
              ) : (
                <Button onClick={() => setConfirmRemove(true)} variant="danger">
                  <Icon.X size={13} /> Remove runner
                </Button>
              )}
            </div>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
